<template>
  <div class="sf-quota-dashboard">
    <div v-if="is_loading" class="loading-overlay">
      <div class="loading-panel">
        <b-spinner small class="mr-2" />
        Loading shipment data...
      </div>
    </div>

    <b-container fluid>
      <h3 class="mt-3 mb-3">SF Shipment Quota Dashboard</h3>

      <!-- Filters -->
      <b-card class="mb-3">
        <b-row>
          <b-col cols="12" sm="6" md="3">
            <b-form-group label="Start Date">
              <b-form-input size="sm" type="date" v-model="filters.start_date" />
            </b-form-group>
          </b-col>
          <b-col cols="12" sm="6" md="3">
            <b-form-group label="End Date">
              <b-form-input size="sm" type="date" v-model="filters.end_date" />
            </b-form-group>
          </b-col>
          <b-col cols="12" sm="6" md="3">
            <b-form-group label="Tracking Status">
              <b-form-select size="sm" v-model="filters.status" :options="status_options" />
            </b-form-group>
          </b-col>
          <b-col cols="12" sm="6" md="3">
            <b-form-group label="Monthly Quota">
              <b-form-input size="sm" type="number" min="0" v-model.number="quota" />
            </b-form-group>
          </b-col>
        </b-row>
        <div class="filter-actions">
          <b-button size="sm" variant="primary" :disabled="is_loading" @click="fetchData">Refresh</b-button>
          <b-button size="sm" variant="outline-secondary" :disabled="is_loading" @click="setThisMonth">This Month</b-button>
        </div>
      </b-card>

      <!-- Quota Progress -->
      <b-row class="mb-3 chart-summary-row">
        <b-col cols="12" md="4" class="mb-3 mb-md-0">
          <b-card class="summary-card">
            <h6>Quota Progress</h6>
            <apexchart type="radialBar" height="260" :options="quotaChartOptions" :series="quotaChartSeries" />
            <p class="text-center mb-0">
              <strong>{{ total_count }}</strong> / {{ quota }} SF trackings
              <span v-if="total_count >= quota" class="text-success"> - Quota met</span>
            </p>
            <p class="text-center text-muted mb-0"><small>Successful trackings, by successful date</small></p>
          </b-card>
        </b-col>
        <b-col cols="12" md="4" class="mb-3 mb-md-0">
          <b-card class="summary-card">
            <h6>Completed vs Pending</h6>
            <apexchart type="donut" height="260" :options="statusSplitChartOptions" :series="statusSplitChartSeries" />
          </b-card>
        </b-col>
        <b-col cols="12" md="4">
          <b-card class="summary-card">
            <h6>By Tracking Status</h6>
            <apexchart type="bar" height="260" :options="byStatusChartOptions" :series="byStatusChartSeries" />
          </b-card>
        </b-col>
      </b-row>

      <!-- Trend -->
      <b-row class="mb-3">
        <b-col cols="12">
          <b-card title="SF Trackings by Date">
            <apexchart type="bar" height="300" :options="byDateChartOptions" :series="byDateChartSeries" />
          </b-card>
        </b-col>
      </b-row>

      <!-- Per client breakdown -->
      <b-card class="mb-3">
        <h6 class="mb-3">By Client</h6>
        <b-table responsive small outlined striped hover :items="by_client" :fields="client_fields">
          <template v-slot:cell(daily_avg)="data">
            {{ dailyAverage(data.item) }}
          </template>
          <template v-slot:cell(completion_rate)="data">
            {{ completionRate(data.item) }}%
          </template>
        </b-table>
      </b-card>
    </b-container>
  </div>
</template>

<script>
import moment from "moment";

const client_fields = [
  { key: "client_name", label: "Client" },
  { key: "qty", label: "Qty" },
  { key: "daily_avg", label: "Daily Avg" },
  { key: "completed_count", label: "Completed" },
  { key: "pending_count", label: "Pending" },
  { key: "completion_rate", label: "Completion %" }
];

export default {
  name: "SfShipmentQuotaDashboard",
  inject: ["API_HOST"],
  data() {
    return {
      quota: 3000,
      filters: {
        start_date: moment().startOf("month").format("YYYY-MM-DD"),
        end_date: moment().endOf("month").format("YYYY-MM-DD"),
        status: null
      },
      status_options: [
        { value: null, text: "All Status" },
        { value: "Pending", text: "Pending" },
        { value: "In_Transit", text: "In_Transit" },
        { value: "Successful", text: "Successful" },
        { value: "Cancelled", text: "Cancelled" },
        { value: "Shipped_Ext", text: "Shipped_Ext" },
        { value: "Returned_to_Warehouse", text: "Returned_to_Warehouse" },
        { value: "Rescheduled", text: "Rescheduled" },
        { value: "Unsuccessful_Delay", text: "Unsuccessful_Delay" },
        { value: "Unsuccessful_Other", text: "Unsuccessful_Other" },
        { value: "Unsuccessful_Reject", text: "Unsuccessful_Reject" },
        { value: "Hold", text: "Hold" },
        { value: "Unsuccessful_Unreach", text: "Unsuccessful_Unreach" },
        { value: "Ready_for_Shipment", text: "Ready_for_Shipment" }
      ],
      total_count: 0,
      completed_count: 0,
      pending_count: 0,
      by_status: [],
      by_client: [],
      by_date: [],
      client_fields,
      quotaChartSeries: [0],
      quotaChartOptions: {},
      statusSplitChartSeries: [0, 0],
      statusSplitChartOptions: {},
      byStatusChartSeries: [],
      byStatusChartOptions: {},
      byDateChartSeries: [],
      byDateChartOptions: {},
      is_loading: false
    };
  },
  created() {
    this.fetchData();
  },
  methods: {
    setThisMonth() {
      this.filters.start_date = moment().startOf("month").format("YYYY-MM-DD");
      this.filters.end_date = moment().endOf("month").format("YYYY-MM-DD");
      this.fetchData();
    },
    completionRate(item) {
      if (!item.qty || item.qty == 0) return 0;
      return ((item.completed_count / item.qty) * 100).toFixed(1);
    },
    dailyAverage(item) {
      const days = this.selectedPeriodDays();
      if (!item.qty || days <= 0) return "0.0";
      return (item.qty / days).toFixed(1);
    },
    selectedPeriodDays() {
      const start = moment(this.filters.start_date);
      const end = moment(this.filters.end_date);
      if (!start.isValid() || !end.isValid() || end.isBefore(start)) return 0;
      return end.diff(start, "days") + 1;
    },
    async fetchData() {
      this.is_loading = true;
      try {
        const response = await this.$http({
          method: "POST",
          baseURL: `${this.API_HOST}`,
          url: "sf_shipment_quota_get.php",
          data: this.filters
        });

        const data = response.data || {};
        this.total_count = data.total_count || 0;
        this.completed_count = data.completed_count || 0;
        this.pending_count = data.pending_count || 0;
        this.by_status = data.by_status || [];
        this.by_client = data.by_client || [];
        this.by_date = data.by_date || [];

        this.prepareQuotaChart();
        this.prepareStatusSplitChart();
        this.prepareByStatusChart();
        this.prepareByDateChart();
      } finally {
        this.is_loading = false;
      }
    },
    prepareQuotaChart() {
      const pct = this.quota > 0 ? Math.min(100, Math.round((this.total_count / this.quota) * 100)) : 0;
      this.quotaChartSeries = [pct];
      this.quotaChartOptions = {
        chart: { type: "radialBar" },
        plotOptions: {
          radialBar: {
            hollow: { size: "60%" },
            dataLabels: {
              value: {
                formatter: () => `${this.total_count} / ${this.quota}`,
                fontSize: "16px"
              }
            }
          }
        },
        colors: [pct >= 100 ? "#00E396" : "#008FFB"],
        labels: ["Quota"]
      };
    },
    prepareStatusSplitChart() {
      this.statusSplitChartSeries = [this.completed_count, this.pending_count];
      this.statusSplitChartOptions = {
        chart: { type: "donut" },
        labels: ["Completed", "Pending"],
        colors: ["#00E396", "#FEB019"],
        legend: { position: "bottom" }
      };
    },
    prepareByStatusChart() {
      const labels = this.by_status.map(row => row.status || "-");
      const counts = this.by_status.map(row => parseInt(row.cnt, 10));

      this.byStatusChartSeries = [{ name: "Trackings", data: counts }];
      this.byStatusChartOptions = {
        chart: { type: "bar" },
        plotOptions: { bar: { horizontal: true } },
        xaxis: { categories: labels },
        colors: ["#008FFB"]
      };
    },
    prepareByDateChart() {
      const labels = this.by_date.map(row => moment(row.tracking_date).format("DD MMM"));
      const counts = this.by_date.map(row => parseInt(row.cnt, 10));

      this.byDateChartSeries = [{ name: "SF Trackings", data: counts }];
      this.byDateChartOptions = {
        chart: { type: "bar" },
        xaxis: {
          categories: labels,
          labels: { rotate: -30, rotateAlways: true, style: { fontSize: "12px" } }
        },
        colors: ["#008FFB"]
      };
    }
  }
};
</script>

<style scoped>
.sf-quota-dashboard {
  position: relative;
}

.filter-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.chart-summary-row {
  align-items: stretch;
}

.chart-summary-row > [class*="col-"] {
  display: flex;
}

.summary-card {
  width: 100%;
  min-height: 360px;
}

.summary-card >>> .card-body {
  display: flex;
  flex-direction: column;
}

.summary-card .apexcharts-canvas {
  margin: auto;
}

.loading-overlay {
  align-items: center;
  background: rgba(255, 255, 255, 0.72);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 2000;
}

.loading-panel {
  align-items: center;
  background: #ffffff;
  border: 1px solid #d9e2ec;
  border-radius: 0.35rem;
  box-shadow: 0 0.5rem 1.5rem rgba(15, 23, 42, 0.14);
  color: #263238;
  display: flex;
  font-weight: 600;
  padding: 0.85rem 1.1rem;
}
</style>



// WEBPACK FOOTER //
// src/components/SfShipmentQuotaDashboard.vue